import { useToast } from '@/hooks/useToast';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';

const VARIANT_STYLES = {
    success: {
        bg: 'bg-emerald-50 dark:bg-emerald-500/15',
        border: 'border-emerald-300 dark:border-emerald-500/40',
        icon: CheckCircle2,
        iconColor: 'text-emerald-600 dark:text-emerald-400',
        title: 'text-emerald-900 dark:text-emerald-200',
        desc: 'text-emerald-700 dark:text-emerald-300',
    },
    error: {
        bg: 'bg-red-50 dark:bg-red-500/15',
        border: 'border-red-300 dark:border-red-500/40',
        icon: XCircle,
        iconColor: 'text-red-600 dark:text-red-400',
        title: 'text-red-900 dark:text-red-200',
        desc: 'text-red-700 dark:text-red-300',
    },
    warning: {
        bg: 'bg-amber-50 dark:bg-amber-500/15',
        border: 'border-amber-300 dark:border-amber-500/40',
        icon: AlertTriangle,
        iconColor: 'text-amber-600 dark:text-amber-400',
        title: 'text-amber-900 dark:text-amber-200',
        desc: 'text-amber-700 dark:text-amber-300',
    },
    info: {
        bg: 'bg-blue-50 dark:bg-blue-500/15',
        border: 'border-blue-300 dark:border-blue-500/40',
        icon: Info,
        iconColor: 'text-blue-600 dark:text-blue-400',
        title: 'text-blue-900 dark:text-blue-200',
        desc: 'text-blue-700 dark:text-blue-300',
    },
};

/**
 * Drop-in toast container — renders at fixed bottom-right.
 * Mount once at the app root level (e.g. in AppProviders).
 */
export function ToastContainer() {
    const { items, dismiss } = useToast();

    if (items.length === 0) return null;

    return (
        <div
            aria-live="polite"
            aria-label="Notifications"
            className="fixed bottom-4 right-4 z-[9999] flex w-full max-w-sm flex-col-reverse gap-2"
        >
            {items.map((t) => {
                const v = VARIANT_STYLES[t.variant] ?? VARIANT_STYLES.info;
                const Icon = v.icon;
                return (
                    <div
                        key={t.id}
                        role="alert"
                        className={`animate-in slide-in-from-right-full fade-in pointer-events-auto flex items-start gap-3 rounded-lg border px-4 py-3 shadow-lg backdrop-blur-sm ${v.bg} ${v.border}`}
                        style={{
                            animation: 'toast-slide-in 0.3s ease-out',
                        }}
                    >
                        <Icon className={`mt-0.5 h-5 w-5 flex-shrink-0 ${v.iconColor}`} />
                        <div className="min-w-0 flex-1">
                            <p className={`text-sm font-semibold ${v.title}`}>{t.title}</p>
                            {t.description ? (
                                <p className={`mt-0.5 text-xs leading-relaxed ${v.desc}`}>
                                    {t.description}
                                </p>
                            ) : null}
                        </div>
                        <button
                            type="button"
                            onClick={() => dismiss(t.id)}
                            className="flex-shrink-0 rounded p-0.5 text-muted-foreground/60 transition-colors hover:text-foreground"
                            aria-label="Dismiss"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                );
            })}
        </div>
    );
}

export default ToastContainer;
